import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { effectivePlan } from "@/lib/effectivePlan";
import { Sparkles, ShieldCheck, ArrowRight } from "lucide-react";

// Phase 2Z.aa - Plan guidance tile on the Overview / Dashboard.
//
// Renders a single small card whose copy depends on the customer's
// effective plan:
//   - "starter"           : "Build your rota from Roster" + upsell to Growth
//   - "growth"            : Soft note that AI + Autopilot are Command
//   - "trial-command"     : "You're on full Command access during your trial"
//   - "founder-command"   : "You're on full Command-level founder access"
//   - everything else     : render nothing (no tile)
export default function PlanGuidanceTile() {
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/billing/me").then(r => setMe(r.data)).catch(() => {});
  }, []);

  if (!me) return null;
  const plan = effectivePlan(me);
  if (plan === "unknown" || plan === "restricted") return null;

  if (plan === "starter") {
    return (
      <Card testid="dashboard-plan-tile-starter"
            tone="amber"
            Icon={ShieldCheck}
            eyebrow="Starter plan"
            title="Build your rota from Roster"
            body="You can still create, edit and publish rotas manually from the Roster page. Advanced planning tools like Rota Style, Coverage Guard, Rota Mix Intelligence and Performance Focus are available on Growth and Command."
            primary={{ to: "/app/roster", label: "Open Roster" }}
            secondary={{ to: "/pricing", label: "Compare plans" }} />
    );
  }

  if (plan === "growth") {
    return (
      <Card testid="dashboard-plan-tile-growth"
            tone="blue"
            Icon={Sparkles}
            eyebrow="Growth plan"
            title="AI Assistant and Autopilot are Command features"
            body="You have full access to Coverage Guard, Rota Style, Pattern Engine, Rota Mix Intelligence, Performance Focus, CSV import and RMI scope detection. Upgrade to Command if you want AI-assisted rota suggestions and Autopilot."
            primary={{ to: "/pricing", label: "Compare plans" }}
            secondary={{ to: "/features#command", label: "See Command features" }} />
    );
  }

  if (plan === "trial-command") {
    return (
      <Card testid="dashboard-plan-tile-trial"
            tone="emerald"
            Icon={Sparkles}
            eyebrow="Free trial"
            title="You're on full Command access during your trial"
            body="Every feature is unlocked for 14 days, including AI Assistant and Autopilot. Pick a plan from Billing whenever you're ready."
            primary={{ to: "/app/billing", label: "Pick a plan" }}
            secondary={{ to: "/features", label: "See all features" }} />
    );
  }

  if (plan === "founder-command") {
    return (
      <Card testid="dashboard-plan-tile-founder"
            tone="emerald"
            Icon={Sparkles}
            eyebrow="Founding Customer"
            title="You're on full Command-level founder access"
            body="Every feature is unlocked during your 3-month founder period, including AI Assistant and Autopilot. Pick a plan from Billing whenever you're ready."
            primary={{ to: "/app/billing", label: "Pick a plan" }}
            secondary={{ to: "/features", label: "See all features" }} />
    );
  }

  return null;
}

function Card({ testid, tone, Icon, eyebrow, title, body, primary, secondary }) {
  const toneClass = {
    amber:   "bg-[#FEF7E0] border-amber-200",
    blue:    "bg-[#F0F6FF] border-[#DBE7FE]",
    emerald: "bg-emerald-50 border-emerald-200",
  }[tone] || "bg-white border-slate-200";
  return (
    <div
      data-testid={testid}
      className={`mt-6 rounded-xl border p-5 sm:p-6 ${toneClass}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 grid place-items-center shrink-0">
          <Icon className="h-4 w-4 text-[#1D4ED8]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-[#4B5773]">{eyebrow}</div>
          <h3 className="font-display text-lg text-[#0B1A36] mt-0.5">{title}</h3>
          <p className="text-sm text-[#4B5773] mt-1.5 leading-relaxed">{body}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {primary && (
              <Link to={primary.to}
                    data-testid={`${testid}-primary`}
                    className="inline-flex items-center px-3 py-1.5 rounded-md bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold">
                {primary.label}
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            )}
            {secondary && (
              <Link to={secondary.to}
                    data-testid={`${testid}-secondary`}
                    className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-200 bg-white text-[#0B1A36] text-xs font-medium hover:bg-slate-50">
                {secondary.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
